// Copyright (c) Microsoft. All rights reserved.

import { IChatMessage } from '../models/ChatMessage';
import { ServiceInfo } from '../models/ServiceInfo';
import { BaseService } from './BaseService';

interface DocumentDownload {
    url: string;
}

interface DocumentList {
    documents: Document[];
}

export interface Document {
    id: string;
    fileName: string;
    lastUpdated: string;
    partitionCount: number;
}

export class DocumentImportService extends BaseService {
    public importDocumentAsync = async (documents: File[], useContentSafety: boolean, accessToken: string) => {
        const formData = new FormData();
        formData.append('useContentSafety', useContentSafety.toString());
        for (const document of documents) {
            formData.append('formFiles', document);
        }

        return await this.getResponseAsync<IChatMessage>(
            {
                commandPath: 'documents',
                method: 'POST',
                body: formData,
            },
            accessToken,
        );
    };

    public getContentSafetyStatusAsync = async (accessToken: string): Promise<boolean> => {
        const serviceInfo = await this.getResponseAsync<ServiceInfo>(
            {
                commandPath: 'info',
                method: 'GET',
            },
            accessToken,
        );

        return serviceInfo.isContentSafetyEnabled;
    };

    public getDocumentUrlAsync = async (documentId: string, fileName: string, accessToken: string): Promise<string> => {
        const documentDownload = await this.getResponseAsync<DocumentDownload>(
            {
                commandPath: `document/${documentId}/${fileName}`,
                method: 'GET',
            },
            accessToken,
        );

        return documentDownload.url;
    };

    public listDocumentsAsync = async (accessToken: string) => {
        const documentList = await this.getResponseAsync<DocumentList>(
            {
                commandPath: 'documents',
                method: 'GET',
            },
            accessToken,
        );

        return documentList.documents;
    };

    public deleteDocumentAsync = async (documentId: string, accessToken: string): Promise<void> => {
        await this.getResponseAsync(
            {
                commandPath: `document/${documentId}`,
                method: 'DELETE',
            },
            accessToken,
        );
    };
}
